import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquarePlus, Search, SendHorizonal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { ErrorBanner, PortalPage } from './ui';

type ThreadSummary = {
  id: string;
  patientId: string;
  subject: string;
  status: string;
  priority: string;
  lastMessageAt?: string | null;
  updatedAt: string;
  assignedTo?: {
    id: string;
    name: string;
    title?: string | null;
  } | null;
  latestMessage?: {
    id: string;
    senderType: string;
    message: string;
    createdAt: string;
  } | null;
};

type ThreadDetail = {
  id: string;
  patientId: string;
  subject: string;
  status: string;
  priority: string;
  assignedTo?: {
    id: string;
    name: string;
    title?: string | null;
  } | null;
  messages: Array<{
    id: string;
    senderType: string;
    message: string;
    createdAt: string;
    sender?: {
      id: string;
      name: string;
      title?: string | null;
    } | null;
  }>;
};

type ThreadListResponse = { data?: ThreadSummary[] };
type ThreadDetailResponse = { data?: ThreadDetail };

function formatMessageTime(value: string | null | undefined, fallback: string) {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function initials(name?: string | null) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'CT';
}

export default function MessagesPage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { activeContext, buildPortalPath, refreshNonce } = usePortalPatientData();
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [threadDetail, setThreadDetail] = useState<ThreadDetail | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [composerText, setComposerText] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadThreads = useCallback(async () => {
    try {
      setListLoading(true);
      setError(null);
      const response = await fetchJson<ThreadListResponse>(buildPortalPath('/portal/messages/conversations?limit=50'));
      const rows = Array.isArray(response?.data) ? response.data : [];
      setThreads(rows);
      setSelectedThreadId((current) => {
        if (current && rows.some((item) => item.id === current)) return current;
        return rows[0]?.id || '';
      });
    } catch (err: any) {
      setError(err?.message || t('messages.loadError'));
      setThreads([]);
      setSelectedThreadId('');
    } finally {
      setListLoading(false);
    }
  }, [buildPortalPath, fetchJson, t]);

  const loadThreadDetail = useCallback(
    async (threadId: string) => {
      if (!threadId) {
        setThreadDetail(null);
        return;
      }
      try {
        setDetailLoading(true);
        setError(null);
        const response = await fetchJson<ThreadDetailResponse>(buildPortalPath(`/portal/messages/conversations/${threadId}`));
        setThreadDetail(response?.data || null);
      } catch (err: any) {
        setError(err?.message || t('messages.detailLoadError'));
        setThreadDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [buildPortalPath, fetchJson, t],
  );

  useEffect(() => {
    void loadThreads();
  }, [loadThreads, refreshNonce]);

  useEffect(() => {
    void loadThreadDetail(selectedThreadId);
  }, [loadThreadDetail, selectedThreadId]);

  const visibleThreads = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return threads.filter((thread) => {
      const normalizedStatus = thread.status.toLowerCase();
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'open' && normalizedStatus === 'open') ||
        (statusFilter === 'closed' && normalizedStatus !== 'open');
      if (!statusMatch) return false;
      if (!query) return true;
      return [thread.subject, thread.assignedTo?.name || '', thread.latestMessage?.message || '']
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [searchText, statusFilter, threads]);

  const selectedThread =
    threadDetail && threadDetail.id === selectedThreadId
      ? threadDetail
      : null;

  const sendReply = useCallback(async () => {
    const message = composerText.trim();
    if (!selectedThreadId || !message) return;
    try {
      setSending(true);
      setError(null);
      await fetchJson(buildPortalPath(`/portal/messages/conversations/${selectedThreadId}/messages`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      } as any);
      setComposerText('');
      await Promise.all([loadThreads(), loadThreadDetail(selectedThreadId)]);
    } catch (err: any) {
      setError(err?.message || t('messages.sendError'));
    } finally {
      setSending(false);
    }
  }, [buildPortalPath, composerText, fetchJson, loadThreadDetail, loadThreads, selectedThreadId, t]);

  const createThread = useCallback(async () => {
    const subject = newSubject.trim();
    const message = newMessage.trim();
    if (!subject || !message || !activeContext?.canMessage) return;
    try {
      setCreating(true);
      setError(null);
      const response = await fetchJson<{ data?: { id: string } }>('/portal/messages/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: activeContext.patientId,
          subject,
          message,
        }),
      } as any);
      setNewSubject('');
      setNewMessage('');
      setShowCreate(false);
      await loadThreads();
      if (response?.data?.id) {
        setSelectedThreadId(response.data.id);
      }
    } catch (err: any) {
      setError(err?.message || t('messages.createError'));
    } finally {
      setCreating(false);
    }
  }, [activeContext, fetchJson, loadThreads, newMessage, newSubject, t]);

  if (activeContext && !activeContext.canMessage) {
    return (
      <PortalPage title={t('messages.title')} description={t('messages.description')}>
        <ErrorBanner message={t('messages.notEnabled')} />
      </PortalPage>
    );
  }

  return (
    <PortalPage title={t('messages.title')} description={t('messages.description')}>
      {error ? <ErrorBanner message={error} /> : null}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft">
        <div className="grid min-h-[72vh] grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="border-r border-slate-200 bg-slate-50/50">
            <div className="border-b border-slate-200 px-5 py-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-2xl font-semibold tracking-tight text-slate-900">{t('messages.sidebarTitle')}</p>
                  <p className="text-sm text-slate-500">{t('messages.sidebarSubtitle')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreate((value) => !value)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
                >
                  <MessageSquarePlus size={18} />
                </button>
              </div>
              <label className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                <Search size={16} className="text-slate-400" />
                <input
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder={t('messages.searchPlaceholder')}
                  className="w-full border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                />
              </label>
              <div className="flex gap-2">
                {(['all', 'open', 'closed'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setStatusFilter(value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold ${statusFilter === value ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                  >
                    {value === 'all' ? t('messages.filterAll') : value === 'open' ? t('messages.filterOpen') : t('messages.filterClosed')}
                  </button>
                ))}
              </div>
              {showCreate ? (
                <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
                  <input
                    value={newSubject}
                    onChange={(event) => setNewSubject(event.target.value)}
                    placeholder={t('messages.subjectPlaceholder')}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                  <textarea
                    value={newMessage}
                    onChange={(event) => setNewMessage(event.target.value)}
                    placeholder={t('messages.messagePlaceholder')}
                    className="min-h-[88px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreate(false)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void createThread()}
                      disabled={creating || !newSubject.trim() || !newMessage.trim()}
                      className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {creating ? t('messages.creating') : t('messages.newConversation')}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="max-h-[56vh] overflow-auto xl:max-h-[62vh]">
              {listLoading ? <p className="px-5 py-8 text-sm text-slate-500">{t('messages.loadingConversations')}</p> : null}
              {!listLoading && visibleThreads.map((thread) => (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => setSelectedThreadId(thread.id)}
                  className={`flex w-full items-start gap-3 border-b border-slate-100 px-5 py-4 text-left transition hover:bg-white ${selectedThreadId === thread.id ? 'bg-white' : ''}`}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold uppercase text-slate-700">
                    {initials(thread.assignedTo?.name || thread.subject)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">{thread.subject}</p>
                      <span className="shrink-0 text-xs text-slate-400">{formatMessageTime(thread.lastMessageAt || thread.updatedAt, t('common.na'))}</span>
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500">{thread.assignedTo?.name || t('messages.careTeam')}</p>
                    <p className="truncate text-xs text-slate-500">{thread.latestMessage?.message || t('messages.noMessagesYet')}</p>
                  </div>
                </button>
              ))}
              {!listLoading && visibleThreads.length === 0 ? (
                <p className="px-5 py-8 text-sm text-slate-500">{t('messages.noMatches')}</p>
              ) : null}
            </div>
          </aside>

          <section className="flex min-h-0 flex-col bg-white">
            {selectedThread ? (
              <>
                <header className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                  <div>
                    <p className="text-xl font-semibold leading-none tracking-tight text-slate-900">{selectedThread.subject}</p>
                    <p className="mt-1 text-sm text-slate-500">
                      {selectedThread.assignedTo?.name || t('messages.careTeam')}
                      {selectedThread.assignedTo?.title ? ` · ${selectedThread.assignedTo.title}` : ''}
                    </p>
                  </div>
                  <span className="pill pill-info">{selectedThread.status}</span>
                </header>

                <div className="flex-1 space-y-5 overflow-auto bg-white px-8 py-6">
                  {detailLoading ? <p className="text-sm text-slate-500">{t('messages.loadingMessages')}</p> : null}
                  {(selectedThread.messages || []).map((message) => {
                    const mine = message.senderType === 'PATIENT';
                    return (
                      <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-[85%] md:max-w-[560px]">
                          <p className={`mb-2 text-[13px] font-medium text-slate-500 ${mine ? 'text-right' : ''}`}>
                            {mine ? t('messages.you') : message.sender?.name || selectedThread.assignedTo?.name || t('messages.careTeam')} · {formatMessageTime(message.createdAt, t('common.na'))}
                          </p>
                          <div className={`inline-block w-fit max-w-full rounded-2xl border p-4 ${mine ? 'border-blue-100 bg-blue-50/70' : 'border-slate-200 bg-white'}`}>
                            <p className="text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{message.message}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <footer className="border-t border-slate-200 bg-white p-4">
                  <div className="rounded-2xl border border-blue-300 px-4 py-3 shadow-sm">
                    <textarea
                      value={composerText}
                      onChange={(event) => setComposerText(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          void sendReply();
                        }
                      }}
                      placeholder={t('messages.replyPlaceholder')}
                      className="min-h-[56px] w-full resize-none border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                    />
                    <div className="mt-1 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => void sendReply()}
                        disabled={sending || !composerText.trim()}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                      >
                        {sending ? t('messages.sending') : t('messages.send')}
                        <SendHorizonal size={14} />
                      </button>
                    </div>
                  </div>
                </footer>
              </>
            ) : (
              <div className="grid h-full place-items-center p-6 text-slate-500">
                {listLoading ? t('messages.loadingMessages') : t('messages.selectConversation')}
              </div>
            )}
          </section>
        </div>
      </section>
    </PortalPage>
  );
}
